import { createContext, useContext, useEffect } from 'react'
import { setRequestLanguage } from '@/lib/api/client'

export type Lang = 'en' | 'ar'

const STORAGE_KEY = 'umrah:lang'

function readStoredLang(): Lang {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'ar' ? 'ar' : 'en'
  } catch {
    return 'en' // storage blocked (private mode, sandbox): fall back quietly
  }
}

export const initialLang = readStoredLang()
setRequestLanguage(initialLang)

/**
 * A company's own first language (P12): what its workspace and sign-in open in when this browser holds no choice
 * of its own — GET /api/address's `locales.workspace_default`, adopted at boot (main.tsx). A choice the member
 * made still wins: it is stored, and a stored choice is never replaced here.
 */
let adoptedLang: Lang | null = null

export function adoptDefaultLang(lang: Lang): void {
  try {
    if (localStorage.getItem(STORAGE_KEY) !== null) return
  } catch {
    // storage blocked: the company's default applies for this visit
  }
  adoptedLang = lang
  setRequestLanguage(lang)
}

/** The language the interface starts in: the stored choice, else the company's adopted default, else English. */
export const startingLang = (): Lang => adoptedLang ?? initialLang

export function persistLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang)
  } catch {
    // not persisted; the choice still applies for this visit
  }
}

export interface LangState {
  lang: Lang
  setLang: (lang: Lang) => void
  /** Pick the string for the current language; Arabic falls back to English. */
  t: (en: string, ar?: string) => string
}

export const LangContext = createContext<LangState | null>(null)

export function useLang(): LangState {
  const context = useContext(LangContext)
  if (context === null) {
    throw new Error('useLang() must be used inside <LangProvider>.')
  }
  return context
}

/**
 * Applies the language's direction to <html> while the calling screen is
 * mounted, and restores LTR when it unmounts — so right-to-left layout is
 * confined to screens designed for it.
 */
export function useDocumentLanguage(): void {
  const { lang } = useLang()

  useEffect(() => {
    const root = document.documentElement
    root.lang = lang
    root.dir = lang === 'ar' ? 'rtl' : 'ltr'

    return () => {
      root.lang = 'en'
      root.dir = 'ltr'
    }
  }, [lang])
}
